import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users } from 'lucide-react';

interface HeroSectionProps {
  className?: string;
}

type VideoQuality = '1080p' | '1440p' | '4k';

/**
 * Determines the optimal video quality based on screen size and connection speed.
 * Returns the appropriate video resolution for the user's device and network.
 */
const getOptimalVideoQuality = (): VideoQuality => {
  // Get the effective screen width considering device pixel ratio
  const screenWidth = window.innerWidth * (window.devicePixelRatio || 1);

  // Check connection speed if available (Network Information API)
  const connection = (navigator as Navigator & { connection?: { effectiveType?: string; downlink?: number } }).connection;
  const effectiveType = connection?.effectiveType;
  const downlink = connection?.downlink; // Mbps

  // Slow connection: use 1080p regardless of screen size
  if (effectiveType === '2g' || effectiveType === '3g' || (downlink && downlink < 5)) {
    return '1080p';
  }

  // High-resolution display (4K capable) with good connection
  if (screenWidth >= 2560 && (!downlink || downlink >= 10)) {
    return '4k';
  }

  // Medium resolution display (1440p capable)
  if (screenWidth >= 1920 && (!downlink || downlink >= 5)) {
    return '1440p';
  }

  // Default to 1080p for smaller screens or unknown connection
  return '1080p';
};

const videoSources: Record<VideoQuality, string> = {
  '1080p': '/vitly-hero-1080p.mp4',
  '1440p': '/vitly-hero-1440p.mp4',
  '4k': '/vitly-hero-4k.mp4',
};

/**
 * A responsive hero section with video background and clear calls-to-action.
 * Features an ambient video loop with text content positioned for readability.
 * Automatically selects video quality based on screen size and connection speed.
 */
const VitlyHeroSection: React.FC<HeroSectionProps> = ({ className = '' }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoQuality, setVideoQuality] = useState<VideoQuality>(() => getOptimalVideoQuality());

  useEffect(() => {
    // Re-evaluate video quality on window resize
    const handleResize = () => {
      const newQuality = getOptimalVideoQuality();
      if (newQuality !== videoQuality) {
        setVideoQuality(newQuality);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [videoQuality]);

  useEffect(() => {
    // Ensure video plays on load
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        // Autoplay failed - likely due to browser policy
        // Video will still be visible as a poster
      });
    }
  }, [videoQuality]);

  return (
    <section
      className={`relative overflow-hidden bg-slate-900 ${className}`}
      role="banner"
      aria-label="Vitly hero section"
      style={{
        // Height matches video aspect ratio, capped at viewport height, min 600px for content
        height: 'clamp(600px, 56.25vw, 100vh)',
      }}
    >
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .hero-text { animation: fadeInUp 0.8s ease-out forwards; letter-spacing: var(--premium-tracking-display, -0.5px); }
        .hero-text-delay-1 { animation-delay: 0.15s; opacity: 0; }
        .hero-text-delay-2 { animation-delay: 0.3s; opacity: 0; }
        .hero-text-delay-3 { animation-delay: 0.45s; opacity: 0; }
        .premium-cta { border-radius: var(--premium-radius-pill, 9999px); box-shadow: var(--premium-shadow); }
      `}</style>

      {/* Video Background - fills the section completely */}
      <div className="absolute inset-0 w-full h-full">
        <video
          ref={videoRef}
          key={videoQuality}
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover object-center"
        >
          <source src={videoSources[videoQuality]} type="video/mp4" />
        </video>

        {/* Overlay gradient for text readability - stronger on left where text is */}
        <div
          className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/70 to-transparent"
          aria-hidden="true"
        />
      </div>

      {/* Bottom gradient for smooth transition to next section */}
      <div
        className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white dark:from-gray-950 to-transparent z-10"
        aria-hidden="true"
      />

      {/* Main Content Container */}
      <div className="relative z-10 flex items-center h-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="max-w-2xl">
            {/* Main Headline */}
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-6 hero-text"
              style={{ lineHeight: '1.1' }}
            >
              Fair Meetups in{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-cyan-300">
                10 Seconds
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-xl sm:text-2xl lg:text-3xl text-gray-200 mb-8 font-medium leading-relaxed hero-text hero-text-delay-1">
              Find the geographic center of your group and discover nearby venues. No more debates.
            </p>

            {/* Call-to-action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 hero-text hero-text-delay-2">
              <Link
                to="/register"
                className="group premium-cta text-white font-semibold py-4 px-8 shadow-lg hover:shadow-xl hover:shadow-[color-mix(in_srgb,var(--premium-accent-cta)_25%,transparent)] transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-3 text-lg focus:outline-none focus:ring-4 focus:ring-[color-mix(in_srgb,var(--premium-accent-cta)_50%,transparent)]"
                style={{ backgroundColor: 'var(--premium-accent-cta, #0070f3)' }}
                aria-label="Plan your first meetup free with Vitly"
              >
                Plan Your First Meetup Free
                <ArrowRight
                  size={20}
                  className="group-hover:translate-x-1 transition-transform duration-200"
                  aria-hidden="true"
                />
              </Link>

              <Link
                to="/how-it-works"
                className="group border-2 border-white/30 hover:border-white/60 text-white hover:bg-white/10 font-semibold py-4 px-8 premium-cta transition-all duration-200 flex items-center justify-center gap-3 text-lg focus:outline-none focus:ring-4 focus:ring-white/20 backdrop-blur-sm"
                aria-label="See how Vitly works"
              >
                See How It Works
                <Users
                  size={20}
                  className="group-hover:scale-110 transition-transform duration-200"
                  aria-hidden="true"
                />
              </Link>
            </div>

            {/* Trust Signals */}
            <div className="mt-12 flex flex-wrap gap-6 text-gray-300 text-sm hero-text hero-text-delay-3">
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                Free to start
              </span>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 bg-teal-400 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
                No credit card
              </span>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 bg-[var(--premium-accent-cta,#0070f3)] rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
                Guests can RSVP without an account
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VitlyHeroSection;
